// services/meta.ts
import Service from '@ember/service';
import { htmlSafe } from '@ember/template';
import { tracked } from '@glimmer/tracking';
import { next } from '@ember/runloop';

export interface MetaTag {
  name?: string;
  property?: string;
  content: string;
}

export interface ScriptTag {
  type: 'application/ld+json';
  content: string;
  id: string;
}

/**
 * SEO-recommended character limits
 */
const SEO_LIMITS = {
  TITLE: 60,
  DESCRIPTION: 160,
  KEYWORDS: 200,
  OG_TITLE: 70,
  OG_DESCRIPTION: 200,
  TWITTER_TITLE: 70,
  TWITTER_DESCRIPTION: 200,
  MAX_KEYWORDS: 10
};

export default class MetaService extends Service {
  title: string = '';
  description: string = '';
  canonicalUrl: string = '';
  scriptTags: ScriptTag[] = [];
  alternateLanguages: Array<{ lang: string; url: string }> = [];

  // Use Maps to easily find and override specific tags
  private _metaTags = new Map<string, MetaTag>();

  // Default values for common tags
  private readonly _defaultTags: MetaTag[] = [
    // Open Graph defaults
    { property: 'og:type', content: 'website' },
    { property: 'og:image', content: 'https://marcemira.com/img/Marcemira-avatar.svg' },
    { property: 'og:image:alt', content: 'Marcelo Mira - Full-Stack Developer & UX/UI Designer' },
    { property: 'og:locale', content: 'en_US' },
    { property: 'og:site_name', content: 'Marcemira' },
    { property: 'og:image:width', content: '512' },
    { property: 'og:image:height', content: '512' },

    // Twitter card defaults
    { name: 'twitter:card', content: 'summary' },
    { name: 'twitter:image', content: 'https://marcemira.com/img/Marcemira-avatar.svg' },
    { name: 'twitter:image:alt', content: 'Marcelo Mira - Full-Stack Developer & UX/UI Designer' },

    // Enhanced Robot tags
    { name: 'robots', content: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1' },

    // Additional useful meta tags
    { name: 'author', content: 'Marcelo Mira' },
    { name: 'language', content: 'English' },
    { name: 'viewport', content: 'width=device-width, initial-scale=1' },
  ];

  @tracked headContentHtml: string = '';

  constructor(properties?: object) {
    super(properties);
    this.initializeDefaultTags();
  }

  /**
   * Initialize the meta tags map with default values
   */
  initializeDefaultTags() {
    // Clear existing tags
    this._metaTags.clear();

    // Add default tags to the map
    for (const tag of this._defaultTags) {
      const key = tag.name || tag.property;
      if (key) {
        this._metaTags.set(key, tag);
      }
    }
  }

  /**
   * Reset all meta values to default
   */
  reset() {
    this.title = '';
    this.description = '';
    this.canonicalUrl = '';
    this.scriptTags = [];
    this.alternateLanguages = [];
    this.headContentHtml = '';
    this.initializeDefaultTags();
  }

  /**
   * Add or update a single meta tag
   */
  setTag(tagData: MetaTag) {
    const key = tagData.name || tagData.property;
    if (key) {
      // Truncate content based on tag type
      const truncatedContent = this.truncateContentForTag(key, tagData.content);
      this._metaTags.set(key, { ...tagData, content: truncatedContent });
    }
  }

  /**
   * Add or update multiple meta tags at once
   */
  setTags(tags: MetaTag[]) {
    for (const tag of tags) {
      this.setTag(tag);
    }
  }

  /**
   * Get all meta tags as an array
   */
  get tags(): MetaTag[] {
    return Array.from(this._metaTags.values());
  }

  /**
   * Set all meta tags, replacing existing ones
   */
  set tags(newTags: MetaTag[]) {
    this.initializeDefaultTags();
    this.setTags(newTags);
  }

  /**
   * Truncate content based on tag type
   * @param key The tag name or property
   * @param content The content to truncate
   * @returns Truncated content
   */
  truncateContentForTag(key: string, content: string): string {
    if (!content) return content;

    switch (key) {
      case 'keywords':
        return this.truncateText(content, SEO_LIMITS.KEYWORDS);
      case 'og:title':
        return this.truncateText(content, SEO_LIMITS.OG_TITLE);
      case 'og:description':
        return this.truncateText(content, SEO_LIMITS.OG_DESCRIPTION);
      case 'twitter:title':
        return this.truncateText(content, SEO_LIMITS.TWITTER_TITLE);
      case 'twitter:description':
        return this.truncateText(content, SEO_LIMITS.TWITTER_DESCRIPTION);
      default:
        return content;
    }
  }

  /**
   * Truncate text to a specified length with ellipsis
   * @param text Text to truncate
   * @param maxLength Maximum length
   * @returns Truncated text
   */
  truncateText(text: string, maxLength: number): string {
    if (!text || text.length <= maxLength) return text;

    // Truncate at the last space before maxLength to avoid cutting words
    const lastSpace = text.substring(0, maxLength).lastIndexOf(' ');
    if (lastSpace <= 0) {
      // If no space found, just truncate at maxLength
      return text.substring(0, maxLength) + '...';
    }

    return text.substring(0, lastSpace) + '...';
  }

  get titleWithSiteName(): string {
    // Truncate title if needed before adding suffix
    const truncatedTitle = this.truncateText(this.title, SEO_LIMITS.TITLE - ' | Marcemira'.length);
    return `${truncatedTitle} | Marcemira`;
  }

  /**
   * Signal that the meta data is ready to be rendered in the header
   * This will be called by routes after setting all meta data
   */
  renderMetaToHeader() {
    this._generateHeadContent();
  }

  /**
   * Helper to get tag content with proper escaping for HTML
   */
  private _escapeHtml(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /**
   * Generate HTML content for meta tags and store it in headContentHtml
   * @private
   */
  _generateHeadContent() {
    const lines: string[] = [];

    // Add title
    if (this.title) {
      const titleTag = `<title data-meta-service>${this._escapeHtml(this.titleWithSiteName)}</title>`;
      lines.push(titleTag);
    }

    // Add description meta tag if available
    if (this.description) {
      const truncatedDescription = this.truncateText(this.description, SEO_LIMITS.DESCRIPTION);
      lines.push(`<meta name="description" content="${this._escapeHtml(truncatedDescription)}" />`);

      // Also update OG and Twitter description
      this.setTag({ property: 'og:description', content: this.description });
      this.setTag({ name: 'twitter:description', content: this.description });
    }

    // Update OG and Twitter title
    if (this.title) {
      this.setTag({ property: 'og:title', content: this.titleWithSiteName });
      this.setTag({ name: 'twitter:title', content: this.titleWithSiteName });
    }

    // Update OG URL
    if (this.canonicalUrl) {
      this.setTag({ property: 'og:url', content: this.canonicalUrl });
    }

    // Add all meta tags
    for (const tag of this.tags) {
      if (tag.name) {
        lines.push(`<meta name="${tag.name}" content="${this._escapeHtml(tag.content)}" />`);
      } else if (tag.property) {
        lines.push(`<meta property="${tag.property}" content="${this._escapeHtml(tag.content)}" />`);
      }
    }

    // Add canonical URL if available
    if (this.canonicalUrl) {
      lines.push(`<link rel="canonical" href="${this._escapeHtml(this.canonicalUrl)}" />`);
    }

    // Add hreflang tags for alternate languages
    for (const alt of this.alternateLanguages) {
      lines.push(`<link rel="alternate" hreflang="${alt.lang}" href="${this._escapeHtml(alt.url)}" />`);
    }

    // Add x-default hreflang (English as default)
    if (this.alternateLanguages.length > 0 && this.canonicalUrl) {
      lines.push(`<link rel="alternate" hreflang="x-default" href="${this._escapeHtml(this.canonicalUrl)}" />`);
    }

    // Add JSON-LD script tags
    for (const script of this.scriptTags) {
      lines.push(`<script type="application/ld+json" id="${script.id}">${script.content}</script>`);
    }

    const content = lines.join('\n');

    // Use next to avoid update during render errors
    next(() => {
      this.headContentHtml = content;
    });
  }

  /**
   * Get the HTML content as a SafeString for use in templates
   */
  get headContent() {
    return htmlSafe(this.headContentHtml);
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    meta: MetaService;
  }
}

